import React, { useState, useMemo } from 'react';

import { FormTitle, FormTitleAction } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Button, ButtonColor } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { SpinnerModal } from '@components/SpinnerModal';
import { TableCellPropsUnion, Table } from '@components/Table/Table';
import { usePatchCompany } from '@hooks/mutation/dashboard/usePatchCompany';
import { usePostCompanySalesperson } from '@hooks/mutation/dashboard/usePostCompanySalesperson';
import { useSearch } from '@hooks/next/useSearch';
import { useUserList } from '@hooks/queries/dashboard/useUserList';
import { allMemberUserStatus, MemberUser } from '@models/Member';
import { DashboardUserSearchQuery } from '@models/next/User';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { DEFAULT_PAGE_SIZE, addOrdering, formatDateTime } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import { useHistory } from 'react-router-dom';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    title: '無料アカウント登録一覧',
    exportCSV: 'Export CSV',
    companyName: '会社名',
    no: 'No.',
    registerTime: '登録時間',
    customerAssigneeName: '顧客側担当者名',
    cellphone: '電話番号',
    email: 'メール',
    loginStatus: '会員登録',
    from: 'から',
    to: 'まで',
    searchTitle: '絞り込み',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    seeAll: 'ALL' as const,
    selectOneItemAtLeast: '少なくとも1つのアイテムを選択してください',
    reset: 'リセット',
    addMember: 'D2D会員を開設する',
    addMemberInProgress: 'D2D会員開設中',
    addMemberFinished: 'D2D会員開設済',
    staff: 'スタッフ',
    tagSearchInputPlaceholder: '会員番号、会社名やメールなどの検索条件を入力してください',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  inputContainer: {
    ...common.flexInline,
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
  },
  addMemberButton: {
    width: '10rem',
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    ...common.textAlignCenter,
    paddingTop: 5,
  },
}));

const AddMemberButton: React.FC<{ item: MemberUser }> = ({ item }) => {
  const history = useHistory();
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const patchCompany = usePatchCompany(item.id);
  const postCompanySalesperson = usePostCompanySalesperson(item.id);
  const addMemberButtonStringMap: Record<string, string> = {
    未登録: strings.addMember,
    審査中: strings.addMemberInProgress,
    登録済: strings.addMemberFinished,
  };

  const addMemberButtonColor: Record<string, ButtonColor> = {
    未登録: 'accent',
    審査中: 'primary',
    登録済: 'grey',
  };

  return (
    <>
      <SpinnerModal open={patchCompany.isLoading || postCompanySalesperson.isLoading} />
      <Button
        className={classes.addMemberButton}
        color={addMemberButtonColor[item.login_status ?? ''] || 'grey'}
        size="small"
        shape="round"
        disabled={item.login_status === '登録済'}
        text={addMemberButtonStringMap[item.login_status ?? ''] || '-'}
        onClick={() => {
          if (item.login_status === '未登録') {
            patchCompany
              .mutateAsync({ status: '未対応' })
              .then(() =>
                postCompanySalesperson
                  .mutateAsync()
                  .then(() =>
                    history.push(
                      `${url('/admin/members/MembershipFormDetails')}?id=${item.id}&creating=true`,
                    ),
                  ),
              );
          } else if (item.login_status === '審査中') {
            history.push(`${url('/admin/members/MembershipFormDetails')}?id=${item.id}`);
          }
        }}
      />
    </>
  );
};

const UsersSearchForm = () => {
  const { search, updateSearch } = useSearch(DashboardUserSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(DashboardUserSearchQuery, {
    initialOutput: search,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(pick(fields, ['company_name', 'email', 'fullname']), form),
    [],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <TextInput
        field={fields.date_joined_after}
        inputProps={{ placeholder: getFieldLabel(fields.date_joined_after) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.date_joined_before}
        inputProps={{ placeholder: getFieldLabel(fields.date_joined_before) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div style={{ gridColumnEnd: '-1' }} />

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => form.root.setOutput(empty)}
        />
      </div>
    </form>
  );
};

export const Users: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(DashboardUserSearchQuery);

  const query = useMemo(() => {
    return {
      ...search,
      page: search.page ?? 1,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    };
  }, [search]);

  const userListQuery = useUserList(query);
  const { data, isLoading, error } = userListQuery;

  const checklist = useChecklist(
    [],
    (userListQuery.data?.results ?? []).map((r) => r.id),
  );
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

  const actions: FormTitleAction[] = [
    {
      title: strings.exportCSV,
      action: () => {
        setExportDialogOpen(true);
      },
    },
  ];

  const orderingOptions = [
    {
      value: '-date_joined',
      label: strings.fromTheNewTime,
    },
    {
      value: 'date_joined',
      label: strings.fromTheOldTime,
    },
  ];

  const memberUserStatusOptions = useMemo(() => {
    return [strings.seeAll, ...allMemberUserStatus].map((status) => ({
      value: status,
      label: status,
    }));
  }, []);

  const checkedItems = useMemo(() => {
    const items = (data?.results ?? [])
      .filter((item) => checklist.checked(item.id))
      .map((item) => item.id);
    return items;
  }, [data, checklist.checklist]);

  const headColumns: TableCellPropsUnion[] = [
    {
      label: 'checkbox',
      type: 'checkable',
      checked: checklist.allCheckedAndNotEmpty,
      onChange: checklist.toggleCheckAll,
    },
    {
      label: strings.no,
    },
    {
      label: strings.registerTime,
      type: 'selectable',
      options: orderingOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          ordering: addOrdering(query.ordering ?? [], value),
        });
      },
    },
    {
      label: strings.staff,
    },
    {
      label: strings.companyName,
    },
    {
      label: strings.customerAssigneeName,
    },
    {
      label: strings.cellphone,
    },
    {
      label: strings.email,
    },
    {
      label: strings.loginStatus,
      type: 'selectable',
      options: memberUserStatusOptions,
      onChange: (value: any) => {
        const status = String(value);
        if (status === 'ALL') {
          updateSearch({ page: 1, login_status: undefined });
        } else {
          updateSearch({
            page: 1,
            login_status: status,
          });
        }
      },
    },
    {
      label: '',
    },
  ];

  function mapItemToTableRowData(index: number, item: MemberUser): TableCellPropsUnion[] {
    return [
      {
        label: 'checkbox',
        type: 'checkable',
        checked: checklist.checked(item.id),
        onChange: (value: boolean) => checklist.setChecked(item.id, value),
      },
      {
        label: index + 1 + (query.page - 1) * query.page_size,
      },
      {
        label: item.date_joined ? formatDateTime(item.date_joined) : '',
      },
      {
        label: `${item.last_name} ${item.first_name}` || '',
      },
      {
        label: item.company_name ?? '',
      },
      {
        label: item.customer_assignee_name ?? '',
      },
      {
        label: item.cellphone ?? '',
      },
      {
        label: item.email ?? '',
      },
      {
        label: item.login_status ?? '',
      },
      {
        label: <AddMemberButton item={item} />,
      },
    ];
  }

  const bodyRows: TableCellPropsUnion[][] | undefined = data?.results.map((result, index) => {
    return mapItemToTableRowData(index, result);
  });

  return (
    <Layout title={strings.title} loading={isLoading} errors={[error]}>
      {() => (
        <>
          <ExportCSVDialog
            open={exportDialogOpen}
            onClose={() => {
              setExportDialogOpen(false);
            }}
            subject={strings.title}
            baseUrl="/dashboard/user/"
            query={query}
            selectedIds={checkedItems}
          />
          <div className={classes.container}>
            <FormTitle
              title={strings.title}
              actions={actions}
              titleSize="large"
              lineDivider={false}
            />

            <UsersSearchForm />

            <PageSizeForm />

            <div style={{ height: 16 }} />

            <Table>
              <Table.Head>
                <Table.Row columns={headColumns} />
              </Table.Head>
              <Table.Body>
                {bodyRows?.map((columns, index) => {
                  return <Table.Row key={index} columns={columns} />;
                })}
              </Table.Body>
            </Table>
            <br />
            <div className={classes.paginationContainer}>
              <PaginationWithSearchQuery query={userListQuery} pageSize={query.page_size} />
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};
